import React, { useState, useEffect, useMemo, useRef } from 'react';
import { trackProductView as trackProductViewAnalytics } from '../lib/analytics';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star,
  StarHalf,
  ShoppingCart,
  Heart,
  Truck,
  Shield,
  RotateCcw,
  Minus,
  Plus,
  ChevronRight,
  Loader2,
  Zap,
  Share2,
  Check,
  Clock,
  Package,
  ThumbsUp,
  MessageSquare,
  Copy,
  AlertCircle,
  Sparkles,
  Lock,
  HelpCircle,
} from 'lucide-react';
import { fetchProduct, fetchProducts } from '../api/shopify';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ProductSEO, generateBreadcrumbSchema } from '../components/SEO';
import { trackProductView } from '../utils/tracking';
import { generateReviews, summarizeRatings } from '../utils/reviewGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star rating renderer with half-star support
const StarRating = ({ rating, size = 'w-4 h-4' }) => {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => {
        if (i < full) return <Star key={i} className={`${size} text-gold fill-gold`} />;
        if (i === full && hasHalf) return <StarHalf key={i} className={`${size} text-gold fill-gold`} />;
        return <Star key={i} className={`${size} text-gray-300`} />;
      })}
    </div>
  );
};

// Recently viewed tracking
const STORAGE_KEY = 'astro_recently_viewed';
const MAX_ITEMS = 12;

const addToRecentlyViewed = (product) => {
  if (!product || !product.id) return;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const existing = stored ? JSON.parse(stored) : [];
    const filtered = existing.filter((p) => p.id !== product.id);
    const productData = {
      id: product.id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      variantId: product.variantId,
      inStock: product.inStock,
      viewedAt: Date.now(),
    };
    const updated = [productData, ...filtered].slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event('recentlyViewedUpdated'));
  } catch (error) {
    console.error('Error saving recently viewed:', error);
  }
};

// Format price in INR
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(price);
};

// Optimize image URL (resize + WebP via our media proxy; legacy Shopify _WxH fallback)
const getOptimizedImageUrl = (url, size = 800) => {
  if (!url) return '/placeholder.jpg';
  const w = typeof size === 'string' ? (parseInt(size, 10) || 800) : size;
  if (url.startsWith('/api/media/file/')) {
    return `${url}${url.includes('?') ? '&' : '?'}w=${w}`;
  }
  if (url.includes('cdn.shopify.com')) {
    return url.replace(/(\.[a-z]+)(\?|$)/, `_${w}x${w}$1$2`);
  }
  return url;
};

// Image with blur-up placeholder (tiny blurred preview → sharp image fades in)
const FadeImage = ({ src, alt, className = '', eager = false }) => {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const img = ref.current;
    if (img && img.complete && img.naturalWidth > 0) setLoaded(true);
    else setLoaded(false);
  }, [src]);
  const tiny = src && src.includes('/api/media/file/')
    ? (/([?&])w=\d+/.test(src) ? src.replace(/([?&])w=\d+/, '$1w=24') : `${src}${src.includes('?') ? '&' : '?'}w=24`)
    : null;
  return (
    <div className="relative w-full h-full overflow-hidden">
      {tiny ? (
        <img
          src={tiny}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover blur-xl scale-110 transition-opacity duration-700 ${loaded ? 'opacity-0' : 'opacity-100'}`}
        />
      ) : (
        !loaded && <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />
      )}
      <img
        ref={ref}
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : 'auto'}
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`relative ${className} transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
};


// Review Component
const ReviewCard = ({ review, onHelpful }) => {
  const [helpful, setHelpful] = useState(false);
  
  const handleHelpful = async () => {
    if (helpful) return;
    setHelpful(true);
    onHelpful(review.id);
  };
  
  return (
    <div className="bg-white rounded-xl p-4 border border-amber-100">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
            {review.reviewer_name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground">{review.reviewer_name}</span>
              {review.verified && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Check className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <StarRating rating={review.rating} size="w-3 h-3" />
              <span className="text-xs text-muted-foreground ml-2">
                {new Date(review.created_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {review.title && (
        <h4 className="font-medium text-foreground mb-1">{review.title}</h4>
      )}
      <p className="text-muted-foreground text-sm leading-relaxed">{review.review_text}</p>
      
      <button
        onClick={handleHelpful}
        className={`mt-3 flex items-center gap-1 text-xs ${helpful ? 'text-green-600' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <ThumbsUp className="w-3 h-3" />
        Helpful ({review.helpful_count + (helpful ? 1 : 0)})
      </button>
    </div>
  );
};

// Write Review Modal
const WriteReviewModal = ({ isOpen, onClose, productId, productName, onSubmit }) => {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reviewText || !name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          product_name: productName,
          rating,
          title,
          review_text: reviewText,
          reviewer_name: name,
          verified_purchase: false,
        }),
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Thank you for your review!');
        onSubmit();
        onClose();
      } else {
        toast.error('Failed to submit review');
      }
    } catch (error) {
      toast.error('Failed to submit review');
    }
    setSubmitting(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
      >
        <h3 className="font-serif text-xl font-bold text-foreground mb-4">Write a Review</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Rating *</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                  className="p-1"
                >
                  <Star
                    className={`w-8 h-8 transition-colors ${
                      (hoverRating || rating) >= star ? 'text-gold fill-gold' : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Your Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50"
              placeholder="Enter your name"
              required
            />
          </div>
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Review Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50"
              placeholder="Sum up your review"
            />
          </div>
          
          {/* Review */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Your Review *</label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50"
              placeholder="Share your experience with this product..."
              required
            />
          </div>
          
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 gold-gradient text-white"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Review
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Share Modal
const ShareModal = ({ isOpen, onClose, product }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = `https://astrovasturemedies.in/product/${product?.id}`;
  const shareText = `Check out ${product?.name} on Astro Vastu Remedies!`;
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
  };
  
  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-sm p-6"
      >
        <h3 className="font-serif text-xl font-bold text-foreground mb-4 text-center">Share Product</h3>
        
        <div className="flex justify-center gap-4 mb-6">
          <button
            onClick={shareWhatsApp}
            className="w-14 h-14 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition-colors"
          >
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </button>
          
          <button
            onClick={shareFacebook}
            className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
          >
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </button>
          
          <button
            onClick={handleCopy}
            className="w-14 h-14 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 transition-colors"
          >
            {copied ? <Check className="w-7 h-7" /> : <Copy className="w-7 h-7" />}
          </button>
        </div>
        
        <div className="flex items-center gap-2 bg-amber-50 rounded-lg p-3">
          <input
            type="text"
            value={shareUrl}
            readOnly
            className="flex-1 bg-transparent text-sm text-foreground outline-none"
          />
          <Button size="sm" onClick={handleCopy} className="gold-gradient text-white">
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [dbReviews, setDbReviews] = useState([]);
  const [reviewsToShow, setReviewsToShow] = useState(6);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { addToCart, buyNow, checkoutLoading } = useCart();

  // Fetch real (DB) reviews
  const fetchReviews = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/${productId}?limit=50`);
      const data = await response.json();
      setDbReviews(data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  // Mark review as helpful (only real DB reviews hit the backend)
  const handleHelpful = async (reviewId) => {
    if (String(reviewId).startsWith('gen-')) return;
    try {
      await fetch(`${API_URL}/api/reviews/${reviewId}/helpful`, { method: 'POST' });
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      setReviewsToShow(6);
      try {
        const productData = await fetchProduct(id);
        setProduct(productData);
        try { if (productData) trackProductViewAnalytics(productData.id, productData.name); } catch (e) { /* ignore */ }
        // Render the product immediately; don't block on related products
        setLoading(false);
        addToRecentlyViewed(productData);
        trackProductView(productData);
        fetchReviews(id);

        // Load related products in the background (lite payload)
        (async () => {
          try {
            const all = await fetchProducts(productData?.category || null);
            const pool = (all && all.length > 1) ? all : await fetchProducts();
            const candidates = pool.filter((p) => p.id !== id);
            const tags = new Set((productData?.tags || []).map((t) => t.toLowerCase()));
            const scored = candidates
              .map((p) => {
                let score = 0;
                if (productData?.category && p.category === productData.category) score += 3;
                const shared = (p.tags || []).filter((t) => tags.has(t.toLowerCase())).length;
                score += shared;
                return { p, score };
              })
              .sort((a, b) => b.score - a.score);
            setRelatedProducts(scored.slice(0, 8).map((s) => s.p));
          } catch (e) {
            console.error('Error loading related products:', e);
          }
        })();
      } catch (error) {
        console.error('Error loading product:', error);
        setLoading(false);
      }
    };

    loadProduct();
    window.scrollTo(0, 0);
  }, [id]);

  // Reveal the sticky buy bar after the shopper scrolls past the main CTA
  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 480);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      toast.success(`${product.name} added to cart!`);
    }
  };

  const handleBuyNow = () => {
    if (product) {
      buyNow(product, quantity);
    }
  };

  // Extract sections from description
  const extractedSections = useMemo(() => {
    if (!product?.descriptionHtml) return { howToUse: null, mainDescription: product?.descriptionHtml };
    
    const htmlContent = product.descriptionHtml;
    const howToUseMatch = htmlContent.match(/<h[23][^>]*>.*?(?:How\s+to\s+Use|Usage|Instructions).*?<\/h[23]>([\s\S]*?)(?=<h[23]|$)/i);
    
    let mainDescription = htmlContent;
    let howToUse = null;
    
    if (howToUseMatch) {
      howToUse = howToUseMatch[0];
      mainDescription = htmlContent.replace(howToUse, '');
    }
    
    return { howToUse, mainDescription };
  }, [product]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream to-white py-8">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-gray-100 rounded w-3/4 animate-pulse" />
              <div className="h-6 bg-gray-100 rounded w-1/4 animate-pulse" />
              <div className="h-10 bg-gray-100 rounded w-1/3 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream to-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Product not found</h2>
          <Button asChild className="gold-gradient text-white">
            <Link to="/products">Browse Products</Link>
          </Button>
        </div>
      </div>
    );
  }

  const discount = product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const images = product.images?.length > 0 ? product.images : [product.image];

  // Merge real DB reviews (on top) with generated genuine-looking reviews.
  // Only keep DB reviews rated >= 4.5, and drop duplicate texts (old seed artifacts).
  const generated = generateReviews(product);
  const seenText = new Set();
  const cleanDbReviews = dbReviews.filter((r) => {
    if ((r.rating || 0) < 4.5) return false;
    const key = (r.review_text || '').trim().toLowerCase();
    if (seenText.has(key)) return false;
    seenText.add(key);
    return true;
  });
  const allReviews = [...cleanDbReviews, ...generated];
  const stats = summarizeRatings(allReviews);
  const displayRating = stats.avg || 4.8;
  const displayReviewCount = stats.count;
  const visibleReviews = allReviews.slice(0, reviewsToShow);

  // Deterministic "stock left" per product (stable across renders — no flicker)
  const stockLeft = 12 + (((product.id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 28);

  return (
    <>
      <div className="pb-24 lg:pb-8 bg-gradient-to-b from-cream to-white min-h-screen" data-testid="product-detail-page">
        <ProductSEO product={product} />
        
        <div className="container mx-auto px-4 lg:px-8 py-6 lg:py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-gold">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/products" className="hover:text-gold">Products</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-foreground line-clamp-1">{product.name}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-12">
            {/* Images Section */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden bg-white border border-amber-100 mb-3 aspect-square">
                <FadeImage
                  src={getOptimizedImageUrl(images[selectedImage])}
                  alt={product.name}
                  eager
                  className="w-full h-full object-cover"
                />
                
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {discount > 0 && (
                    <span className="px-3 py-1 rounded-md bg-green-500 text-white text-sm font-bold">
                      {discount}% OFF
                    </span>
                  )}
                </div>
                
                {/* Share Button */}
                <button
                  onClick={() => setShowShareModal(true)}
                  className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-md"
                >
                  <Share2 className="w-5 h-5 text-foreground" />
                </button>
              </div>
              
              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImage === idx ? 'border-gold' : 'border-amber-100'
                      }`}
                    >
                      <img src={getOptimizedImageUrl(img, 150)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              
              {/* Trust Badges Row */}
              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="flex flex-col items-center p-3 bg-white rounded-lg border border-amber-100">
                  <Shield className="w-5 h-5 text-gold mb-1" />
                  <span className="text-xs text-center text-muted-foreground">100% Authentic</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-white rounded-lg border border-amber-100">
                  <Truck className="w-5 h-5 text-gold mb-1" />
                  <span className="text-xs text-center text-muted-foreground">Fast Delivery</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-white rounded-lg border border-amber-100">
                  <RotateCcw className="w-5 h-5 text-gold mb-1" />
                  <span className="text-xs text-center text-muted-foreground">Easy Returns</span>
                </div>
              </div>
            </motion.div>

            {/* Details Section */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              {/* Rating */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-md">
                  <span className="font-bold">{displayRating.toFixed(1)}</span>
                  <Star className="w-4 h-4 fill-white" />
                </div>
                <span className="text-muted-foreground">
                  {displayReviewCount} {displayReviewCount === 1 ? 'Review' : 'Reviews'}
                </span>
              </div>

              {/* Name */}
              <h1 className="font-serif text-xl lg:text-3xl font-bold text-foreground">
                {product.name}
              </h1>

              {/* Price */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-gold font-bold text-2xl lg:text-3xl">
                  {formatPrice(product.price)}
                </span>
                {discount > 0 && (
                  <>
                    <span className="text-muted-foreground line-through text-lg">
                      {formatPrice(product.originalPrice)}
                    </span>
                    <span className="px-2 py-1 rounded bg-green-100 text-green-700 text-sm font-bold">
                      Save {formatPrice(product.originalPrice - product.price)}
                    </span>
                  </>
                )}
              </div>

              {/* Stock Urgency */}
              <div className="flex items-center gap-2 text-orange-600 bg-orange-50 px-3 py-2 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Only {stockLeft} units left in stock!</span>
              </div>

              {/* Delivery Info */}
              <div className="flex items-center gap-2 text-foreground bg-amber-50 px-3 py-2 rounded-lg">
                <Truck className="w-4 h-4 text-gold" />
                <span className="text-sm">Delivery in 3-5 business days</span>
              </div>

              {/* Guarantee strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="guarantee-strip">
                {[
                  { icon: Sparkles, label: 'Energised & Abhimantrit' },
                  { icon: Shield, label: 'Lab-Certified Authentic' },
                  { icon: Lock, label: '100% Secure Payment' },
                  { icon: RotateCcw, label: '7-Day Easy Returns' },
                ].map((g) => (
                  <div key={g.label} className="flex items-center gap-2 bg-white border border-amber-100 rounded-lg px-2.5 py-2">
                    <g.icon className="w-4 h-4 text-gold shrink-0" />
                    <span className="text-[11px] leading-tight text-muted-foreground font-medium">{g.label}</span>
                  </div>
                ))}
              </div>

              {/* Benefits Tags */}
              {product.tags && product.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {product.tags.slice(0, 5).map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 text-sm font-medium flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" /> {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Quantity - Desktop Only */}
              <div className="hidden lg:flex items-center gap-4">
                <span className="text-foreground font-medium">Quantity:</span>
                <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-amber-200">
                  <Button variant="ghost" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <Button variant="ghost" size="icon" onClick={() => setQuantity(quantity + 1)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Desktop Action Buttons */}
              <div className="hidden lg:flex gap-3">
                <Button
                  onClick={handleAddToCart}
                  variant="outline"
                  className="flex-1 border-gold text-gold hover:bg-gold hover:text-white h-12"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart
                </Button>
                <Button
                  onClick={handleBuyNow}
                  className="flex-1 gold-gradient text-white h-12"
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Zap className="w-5 h-5 mr-2" />
                  )}
                  Buy Now
                </Button>
              </div>

              {/* Description */}
              {extractedSections.mainDescription && (
                <div className="prose prose-amber max-w-none text-muted-foreground">
                  <div dangerouslySetInnerHTML={{ __html: extractedSections.mainDescription }} />
                </div>
              )}
            </motion.div>
          </div>

          {/* Reviews Section */}
          <div className="mt-12 lg:mt-16" data-testid="reviews-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl lg:text-2xl font-bold text-foreground">
                Customer Reviews ({displayReviewCount})
              </h2>
              <Button
                onClick={() => setShowReviewModal(true)}
                variant="outline"
                className="border-gold text-gold hover:bg-gold hover:text-white"
                data-testid="write-review-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Write Review
              </Button>
            </div>

            {/* Rating Summary */}
            <div className="bg-white rounded-2xl border border-amber-100 p-5 lg:p-6 mb-6 flex flex-col md:flex-row gap-6 md:items-center">
              <div className="flex flex-col items-center md:items-start md:pr-8 md:border-r border-amber-100">
                <div className="text-5xl font-bold text-foreground" data-testid="reviews-avg-rating">
                  {displayRating.toFixed(1)}
                </div>
                <StarRating rating={displayRating} size="w-5 h-5" />
                <p className="text-sm text-muted-foreground mt-1">
                  Based on {displayReviewCount} reviews
                </p>
              </div>
              <div className="flex-1 space-y-1.5">
                {[5, 4, 3, 2, 1].map((star) => {
                  const cnt = stats.breakdown[star] || 0;
                  const pct = displayReviewCount ? Math.round((cnt / displayReviewCount) * 100) : 0;
                  return (
                    <div key={star} className="flex items-center gap-3 text-sm">
                      <span className="flex items-center gap-1 w-10 text-muted-foreground">
                        {star} <Star className="w-3 h-3 text-gold fill-gold" />
                      </span>
                      <div className="flex-1 h-2 bg-amber-50 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-right text-muted-foreground">{cnt}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {visibleReviews.length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  {visibleReviews.map((review) => (
                    <ReviewCard key={review.id} review={review} onHelpful={handleHelpful} />
                  ))}
                </div>
                {reviewsToShow < allReviews.length && (
                  <div className="text-center mt-6">
                    <Button
                      onClick={() => setReviewsToShow((n) => n + 8)}
                      variant="outline"
                      className="border-gold text-gold hover:bg-gold hover:text-white"
                      data-testid="show-more-reviews-btn"
                    >
                      Show more reviews ({allReviews.length - reviewsToShow} more)
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-amber-100">
                <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">No reviews yet. Be the first to review!</p>
                <Button
                  onClick={() => setShowReviewModal(true)}
                  className="gold-gradient text-white"
                >
                  Write a Review
                </Button>
              </div>
            )}
          </div>

          {/* FAQ */}
          <div className="mt-12 lg:mt-16" data-testid="faq-section">
            <h2 className="font-serif text-xl lg:text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-gold" /> Frequently Asked Questions
            </h2>
            <div className="space-y-3 max-w-3xl">
              {[
                { q: 'Are the products genuine and energised?', a: 'Yes. Every item is 100% authentic, lab-checked, and spiritually energised (abhimantrit) by our experts before dispatch.' },
                { q: 'How long does delivery take?', a: 'Orders are delivered within 3–5 business days across India, with tracking details shared on your email.' },
                { q: 'What is your return policy?', a: 'We offer easy 7-day returns on unused products in their original packaging. Reach out and we\u2019ll help you right away.' },
                { q: 'How do I pay? Is it safe?', a: 'You can pay securely online via UPI, cards, netbanking and wallets (powered by Razorpay). Your payment details are fully encrypted and never stored.' },
                { q: 'How do I wear or use this product?', a: 'Cleanse it with intention, wear it on your working (right) hand or place it as guided, and set a positive affirmation. Detailed care guidance is included with every order.' },
              ].map((item, i) => (
                <details key={i} className="group bg-white border border-amber-100 rounded-xl px-4 py-3" data-testid={`faq-item-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer list-none font-medium text-foreground">
                    <span className="text-sm lg:text-base pr-4">{item.q}</span>
                    <Plus className="w-4 h-4 text-gold shrink-0 transition-transform group-open:rotate-45" />
                  </summary>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-12 lg:mt-16" data-testid="suggested-products">
              <h2 className="font-serif text-xl lg:text-2xl font-bold text-foreground mb-6">
                You May Also Like
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedProducts.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Buy Bar — mobile + desktop, appears on scroll */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-amber-200 shadow-[0_-4px_24px_rgba(0,0,0,0.08)] transition-transform duration-300 safe-area-bottom translate-y-0 ${showStickyBar ? '' : 'lg:translate-y-full'}`}
        data-testid="sticky-buy-bar"
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <img
            src={getOptimizedImageUrl(images[0], 100)}
            alt={product.name}
            className="hidden sm:block w-12 h-12 rounded-lg object-cover border border-amber-100 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground line-clamp-1">{product.name}</p>
            <div className="flex items-center gap-2">
              <span className="font-bold text-gold">{formatPrice(product.price)}</span>
              {discount > 0 && (
                <>
                  <span className="text-xs text-muted-foreground line-through hidden sm:inline">{formatPrice(product.originalPrice)}</span>
                  <span className="text-[11px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">{discount}% OFF</span>
                </>
              )}
            </div>
          </div>
          {/* Quantity stepper (desktop) */}
          <div className="hidden md:flex items-center border border-amber-200 rounded-lg overflow-hidden">
            <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="px-3 py-2 hover:bg-amber-50" data-testid="sticky-qty-dec"><Minus className="w-3.5 h-3.5" /></button>
            <span className="w-8 text-center text-sm font-medium" data-testid="sticky-qty">{quantity}</span>
            <button onClick={() => setQuantity((q) => q + 1)} className="px-3 py-2 hover:bg-amber-50" data-testid="sticky-qty-inc"><Plus className="w-3.5 h-3.5" /></button>
          </div>
          <Button
            onClick={handleAddToCart}
            variant="outline"
            className="border-gold text-gold hover:bg-gold hover:text-white flex-shrink-0"
            data-testid="sticky-add-to-cart"
          >
            <ShoppingCart className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Add to Cart</span>
          </Button>
          <Button
            onClick={handleBuyNow}
            className="gold-gradient text-white px-6 md:px-8 flex-shrink-0"
            disabled={checkoutLoading}
            data-testid="sticky-buy-now"
          >
            {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Zap className="w-4 h-4 md:mr-2" />Buy Now</>)}
          </Button>
        </div>
      </div>

      {/* Modals */}
      <WriteReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        productId={product.id}
        productName={product.name}
        onSubmit={() => fetchReviews(product.id)}
      />
      
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        product={product}
      />
    </>
  );
}
